import { useMemo, useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { Search } from 'lucide-react';
import type { SeatingParty } from '@peles/shared';
import { FilterSelect } from '../common/FilterSelect';
import { PartyChip } from './PartyChip';
import { UNSEATED_DROP_ID } from './dropTargets';
import { stillToSeat, type ResponseFilter } from './boardModel';

const PAGE = 50;

const RESPONSE_OPTIONS: { value: ResponseFilter; label: string }[] = [
  { value: 'coming', label: 'Not declined' },
  { value: 'all', label: 'Everyone' },
];

export interface UnseatedPanelProps {
  parties: SeatingParty[];
  filter: ResponseFilter;
  onFilterChange: (filter: ResponseFilter) => void;
  selectedGuestId: string | null;
  proposed: Set<string>;
  canWrite: boolean;
  onSelect: (guestId: string) => void;
}

/**
 * The parties still to seat. A party is dragged from here onto a table, or
 * tapped to pick it up; a seated party dropped here is taken off the plan.
 */
export function UnseatedPanel({
  parties,
  filter,
  onFilterChange,
  selectedGuestId,
  proposed,
  canWrite,
  onSelect,
}: UnseatedPanelProps) {
  const { setNodeRef, isOver } = useDroppable({ id: UNSEATED_DROP_ID, disabled: !canWrite });
  const [query, setQuery] = useState('');
  const [sides, setSides] = useState<string[]>([]);
  const [shown, setShown] = useState(PAGE);

  const sideOptions = useMemo(
    () => [...new Set(parties.map((p) => p.side).filter((s): s is string => !!s))].sort(),
    [parties],
  );
  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    return parties.filter(
      (party) =>
        (!term || party.name.toLowerCase().includes(term)) &&
        (sides.length === 0 || (party.side != null && sides.includes(party.side))),
    );
  }, [parties, query, sides]);
  const people = visible.reduce((sum, party) => sum + stillToSeat(party), 0);

  return (
    <section
      ref={setNodeRef}
      aria-label="Unseated"
      className={`flex flex-col rounded-2xl border bg-white p-3 ${
        isOver ? 'border-primary-400 ring-2 ring-primary-100' : 'border-gray-200'
      }`}
      data-testid="unseated-panel"
    >
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold text-gray-900">
          Unseated <span className="text-gray-500 font-normal">({people})</span>
        </h2>
        <div className="flex rounded-md border border-gray-200 text-xs" role="group">
          {RESPONSE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={filter === option.value}
              onClick={() => onFilterChange(option.value)}
              className={`px-2 py-1 ${
                filter === option.value ? 'bg-gray-900 text-white' : 'text-gray-600'
              } first:rounded-l-md last:rounded-r-md`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex gap-2 mb-2">
        <label className="relative flex-1">
          <Search size={14} className="absolute left-2 top-2.5 text-gray-400" />
          <input
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShown(PAGE);
            }}
            placeholder="Search"
            aria-label="Search unseated guests"
            className="w-full rounded-md border border-gray-300 py-1.5 pl-7 pr-2 text-sm"
          />
        </label>
        {sideOptions.length > 0 && (
          <FilterSelect
            label="Side"
            items={sideOptions}
            value={sides}
            multiple
            onChange={(value) => {
              setSides(value as string[]);
              setShown(PAGE);
            }}
            getValue={(side) => side}
            getLabel={(side) => side}
          />
        )}
      </div>
      <ul className="space-y-1.5 overflow-y-auto max-h-[60vh]" data-testid="unseated-list">
        {visible.slice(0, shown).map((party) => {
          const left = stillToSeat(party);
          return (
            <li key={party.guestId}>
              <PartyChip
                guestId={party.guestId}
                where="unseated"
                name={party.name}
                detail={
                  party.seatsHeld > 0
                    ? `${left} of ${party.partySize} to seat`
                    : `×${party.partySize}`
                }
                side={party.side}
                selected={selectedGuestId === party.guestId}
                proposed={proposed.has(party.guestId)}
                draggable={canWrite}
                onClick={canWrite ? () => onSelect(party.guestId) : undefined}
              />
            </li>
          );
        })}
      </ul>
      {visible.length === 0 && (
        <p className="py-6 text-center text-sm text-gray-500">
          {parties.length === 0 ? 'Everyone is seated.' : 'No one matches.'}
        </p>
      )}
      {visible.length > shown && (
        <button
          type="button"
          onClick={() => setShown((n) => n + PAGE)}
          className="mt-2 text-sm text-primary-700 hover:underline"
        >
          Show more ({visible.length - shown})
        </button>
      )}
    </section>
  );
}
